(() => {
  const RECENT_KEY = 'ss-recently-viewed';

  const readRecent = () => {
    try {
      const value = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  };

  if (!customElements.get('ss-tabs')) {
    customElements.define(
      'ss-tabs',
      class SsTabs extends HTMLElement {
        connectedCallback() {
          this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
          this.list = this.querySelector('[role="tablist"]');
          this.tabs.forEach((tab, index) => {
            tab.addEventListener('click', () => this.select(index));
            tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
          });
          if (this.list) {
            this.indicator = document.createElement('span');
            this.indicator.className = 'ss-tabs__indicator';
            this.indicator.setAttribute('aria-hidden', 'true');
            this.list.prepend(this.indicator);
            this.list.classList.add('has-indicator');
            this.moveIndicator();
            window.addEventListener('resize', () => this.moveIndicator());
            if (document.fonts) document.fonts.ready.then(() => this.moveIndicator());
          }
        }

        moveIndicator() {
          const active = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
          if (!active || !this.indicator) return;
          this.list.style.setProperty('--ss-x', `${active.offsetLeft}px`);
          this.list.style.setProperty('--ss-w', `${active.offsetWidth}px`);
        }

        onKeydown(event, index) {
          let next = null;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === null) return;
          event.preventDefault();
          this.select(next);
          this.tabs[next].focus();
        }

        select(index) {
          this.tabs.forEach((tab, tabIndex) => {
            const selected = tabIndex === index;
            tab.setAttribute('aria-selected', selected ? 'true' : 'false');
            tab.setAttribute('tabindex', selected ? '0' : '-1');
            const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
            if (panel) panel.hidden = !selected;
          });
          this.moveIndicator();
        }
      }
    );
  }

  if (!customElements.get('ss-countdown')) {
    customElements.define(
      'ss-countdown',
      class SsCountdown extends HTMLElement {
        connectedCallback() {
          this.mode = this.dataset.mode || 'daily';
          this.fixedEnd = this.dataset.end ? new Date(this.dataset.end.replace(' ', 'T')) : null;
          this.values = {
            days: this.querySelector('[data-unit="days"]'),
            hours: this.querySelector('[data-unit="hours"]'),
            minutes: this.querySelector('[data-unit="minutes"]'),
            seconds: this.querySelector('[data-unit="seconds"]'),
          };
          this.tick();
          this.timer = setInterval(() => this.tick(), 1000);
        }

        disconnectedCallback() {
          clearInterval(this.timer);
        }

        target() {
          if (this.mode === 'fixed' && this.fixedEnd && !Number.isNaN(this.fixedEnd.getTime())) return this.fixedEnd;
          const end = new Date();
          end.setHours(23, 59, 59, 999);
          return end;
        }

        tick() {
          const diff = this.target().getTime() - Date.now();
          if (diff <= 0) {
            this.hidden = true;
            clearInterval(this.timer);
            return;
          }
          const pad = (value) => String(value).padStart(2, '0');
          const seconds = Math.floor(diff / 1000);
          this.update('days', pad(Math.floor(seconds / 86400)));
          this.update('hours', pad(Math.floor((seconds % 86400) / 3600)));
          this.update('minutes', pad(Math.floor((seconds % 3600) / 60)));
          this.update('seconds', pad(seconds % 60));
        }

        update(unit, value) {
          const el = this.values[unit];
          if (!el || el.textContent === value) return;
          el.textContent = value;
          el.classList.remove('is-tick');
          void el.offsetWidth;
          el.classList.add('is-tick');
          setTimeout(() => el.classList.remove('is-tick'), 350);
        }
      }
    );
  }

  if (!customElements.get('ss-recent-tracker')) {
    customElements.define(
      'ss-recent-tracker',
      class SsRecentTracker extends HTMLElement {
        connectedCallback() {
          const handle = this.dataset.handle;
          if (!handle) return;
          const list = readRecent().filter((item) => item !== handle);
          list.unshift(handle);
          try {
            localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 12)));
          } catch (error) {
            return;
          }
        }
      }
    );
  }

  if (!customElements.get('ss-recently-viewed')) {
    customElements.define(
      'ss-recently-viewed',
      class SsRecentlyViewed extends HTMLElement {
        connectedCallback() {
          const limit = parseInt(this.dataset.limit || '4', 10);
          const current = this.dataset.current || '';
          const handles = readRecent()
            .filter((handle) => handle !== current)
            .slice(0, limit);
          this.grid = this.querySelector('[data-recent-grid]');
          if (!handles.length || !this.grid) {
            if (!this.hasAttribute('data-design-mode')) this.hidden = true;
            return;
          }
          this.load(handles);
        }

        async load(handles) {
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const results = await Promise.all(
            handles.map((handle) =>
              fetch(`${root}products/${encodeURIComponent(handle)}?section_id=ss-card-fragment`)
                .then((response) => (response.ok ? response.text() : ''))
                .catch(() => '')
            )
          );
          const fragment = document.createDocumentFragment();
          results.forEach((html) => {
            if (!html) return;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const card = doc.querySelector('[data-ss-card]');
            if (!card) return;
            const item = document.createElement('li');
            item.className = 'grid__item';
            item.innerHTML = card.innerHTML;
            fragment.appendChild(item);
          });
          if (!fragment.childNodes.length) {
            this.hidden = true;
            return;
          }
          this.grid.appendChild(fragment);
          this.hidden = false;
        }
      }
    );
  }

  if (!customElements.get('ss-popup')) {
    customElements.define(
      'ss-popup',
      class SsPopup extends HTMLElement {
        connectedCallback() {
          this.dialog = this.querySelector('dialog');
          if (!this.dialog) return;
          this.storageKey = `ss-popup-${this.dataset.version || '1'}`;
          this.querySelectorAll('[data-popup-close]').forEach((button) =>
            button.addEventListener('click', () => this.close())
          );
          this.dialog.addEventListener('click', (event) => {
            if (event.target === this.dialog) this.close();
          });
          this.dialog.addEventListener('cancel', () => this.remember());

          if (this.dataset.success === 'true') {
            this.open();
            return;
          }
          if (this.dataset.designMode === 'true') return;
          if (this.seen()) return;
          const delay = parseInt(this.dataset.delay || '6', 10) * 1000;
          this.timer = setTimeout(() => this.open(), delay);

          document.addEventListener('shopify:section:select', (event) => {
            if (this.contains(event.target) || event.target.contains(this)) this.open();
          });
        }

        seen() {
          try {
            return localStorage.getItem(this.storageKey) === 'seen';
          } catch (error) {
            return false;
          }
        }

        remember() {
          try {
            localStorage.setItem(this.storageKey, 'seen');
          } catch (error) {
            return;
          }
        }

        open() {
          if (this.dialog.open) return;
          if (document.querySelector('cart-drawer.active')) return;
          this.dialog.showModal();
        }

        close() {
          this.remember();
          this.dialog.close();
        }
      }
    );
  }

  if (!customElements.get('ss-sticky-atc')) {
    customElements.define(
      'ss-sticky-atc',
      class SsStickyAtc extends HTMLElement {
        connectedCallback() {
          this.source = document.querySelector(this.dataset.target);
          this.button = this.querySelector('button');
          if (!this.source || !this.button || !('IntersectionObserver' in window)) return;
          document.body.classList.add('ss-has-sticky-atc');
          const observer = new IntersectionObserver(
            ([entry]) => {
              const above = entry.boundingClientRect.top < 0;
              this.classList.toggle('is-visible', !entry.isIntersecting && above);
            },
            { threshold: 0 }
          );
          observer.observe(this.source);
          this.button.addEventListener('click', () => {
            const submit = this.source.querySelector('[type="submit"]');
            if (submit && !submit.disabled) {
              submit.click();
            } else {
              this.source.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          });
        }
      }
    );
  }

  if (!customElements.get('ss-whatsapp-form')) {
    customElements.define(
      'ss-whatsapp-form',
      class SsWhatsappForm extends HTMLElement {
        connectedCallback() {
          const trigger = this.querySelector('[data-whatsapp-send]');
          const form = this.querySelector('form');
          if (!trigger || !form) return;
          trigger.addEventListener('click', () => {
            if (!form.reportValidity()) return;
            const lines = [this.dataset.intro || ''];
            form.querySelectorAll('[data-wa-label]').forEach((field) => {
              if (field.value) lines.push(`${field.dataset.waLabel}: ${field.value}`);
            });
            const url = `https://wa.me/${this.dataset.number}?text=${encodeURIComponent(lines.join('\n'))}`;
            window.open(url, '_blank', 'noopener');
          });
        }
      }
    );
  }

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('ss-hero')) {
    customElements.define(
      'ss-hero',
      class SsHero extends HTMLElement {
        connectedCallback() {
          this.slides = Array.from(this.querySelectorAll('.ss-hero__slide'));
          this.dots = Array.from(this.querySelectorAll('.ss-hero__dot'));
          this.counter = this.querySelector('[data-current]');
          this.index = 0;
          this.duration = parseInt(this.dataset.autoplay || '0', 10) * 1000;
          this.style.setProperty('--ss-duration', `${this.duration || 6000}ms`);
          if (this.slides.length < 2) return;
          this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.index), true)));
          const prev = this.querySelector('[data-prev]');
          const next = this.querySelector('[data-next]');
          if (prev) prev.addEventListener('click', () => this.go(this.index - 1, true));
          if (next) next.addEventListener('click', () => this.go(this.index + 1, true));
          this.addEventListener('mouseenter', () => this.pause());
          this.addEventListener('mouseleave', () => this.resume());
          this.addEventListener('focusin', () => this.pause());
          this.addEventListener('focusout', () => this.resume());
          this.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowRight') this.go(this.index + 1, true);
            if (event.key === 'ArrowLeft') this.go(this.index - 1, true);
          });
          let startX = null;
          this.addEventListener('touchstart', (event) => (startX = event.touches[0].clientX), { passive: true });
          this.addEventListener('touchend', (event) => {
            if (startX === null) return;
            const delta = event.changedTouches[0].clientX - startX;
            if (Math.abs(delta) > 45) this.go(this.index + (delta < 0 ? 1 : -1), true);
            startX = null;
          });
          document.addEventListener('visibilitychange', () => (document.hidden ? this.pause() : this.resume()));
          this.go(0);
        }

        disconnectedCallback() {
          clearTimeout(this.timer);
        }

        go(target, manual) {
          const count = this.slides.length;
          this.index = (target + count) % count;
          this.slides.forEach((slide, i) => {
            const active = i === this.index;
            slide.classList.toggle('is-active', active);
            slide.setAttribute('aria-hidden', active ? 'false' : 'true');
            slide.querySelectorAll('a, button').forEach((el) => (el.tabIndex = active ? 0 : -1));
          });
          this.dots.forEach((dot, i) => {
            dot.classList.toggle('is-done', i < this.index);
            dot.classList.remove('is-active');
            dot.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
          });
          const activeDot = this.dots[this.index];
          if (activeDot) {
            void activeDot.offsetWidth;
            activeDot.classList.add('is-active');
          }
          if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');
          if (manual) this.classList.remove('is-paused');
          this.schedule();
        }

        schedule() {
          clearTimeout(this.timer);
          if (!this.duration || reduceMotion || this.paused) return;
          this.startedAt = Date.now();
          this.remaining = this.duration;
          this.timer = setTimeout(() => this.go(this.index + 1), this.remaining);
        }

        pause() {
          if (!this.duration || this.paused) return;
          this.paused = true;
          this.classList.add('is-paused');
          clearTimeout(this.timer);
          this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt));
        }

        resume() {
          if (!this.duration || !this.paused) return;
          this.paused = false;
          this.classList.remove('is-paused');
          this.startedAt = Date.now();
          this.timer = setTimeout(() => this.go(this.index + 1), this.remaining || this.duration);
        }
      }
    );
  }

  if (!customElements.get('ss-back-to-top')) {
    customElements.define(
      'ss-back-to-top',
      class SsBackToTop extends HTMLElement {
        connectedCallback() {
          this.hidden = false;
          this.querySelector('button').addEventListener('click', () => window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' }));
        }
      }
    );
  }

  const progress = document.createElement('div');
  progress.className = 'ss-progress';
  progress.setAttribute('aria-hidden', 'true');
  document.body.appendChild(progress);

  let ticking = false;
  const onScroll = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const ratio = max > 0 ? Math.min(1, window.scrollY / max) : 0;
    document.documentElement.style.setProperty('--ss-scroll', ratio.toFixed(4));
    document.body.classList.toggle('ss-scrolled', window.scrollY > 24);
    const top = document.querySelector('ss-back-to-top');
    if (top) top.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.8);
    ticking = false;
  };
  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(onScroll);
      }
    },
    { passive: true }
  );
  onScroll();

  if (!reduceMotion && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('ss-motion');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    const watch = (root) => root.querySelectorAll('[data-ss-reveal]:not(.is-revealed)').forEach((el) => observer.observe(el));
    watch(document);
    document.addEventListener('shopify:section:load', (event) => watch(event.target));
  }

  const bubbleHost = document.getElementById('cart-icon-bubble');
  if (bubbleHost && 'MutationObserver' in window) {
    new MutationObserver(() => {
      bubbleHost.classList.remove('ss-pop');
      void bubbleHost.offsetWidth;
      bubbleHost.classList.add('ss-pop');
    }).observe(bubbleHost, { childList: true, subtree: true });
  }

  if (!reduceMotion && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.addEventListener('pointermove', (event) => {
      const tile = event.target.closest && event.target.closest('[data-ss-tilt]');
      document.querySelectorAll('[data-ss-tilt].is-tilting').forEach((el) => {
        if (el !== tile) {
          el.classList.remove('is-tilting');
          el.style.transform = '';
        }
      });
      if (!tile) return;
      const rect = tile.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      tile.classList.add('is-tilting');
      tile.style.transform = `perspective(900px) rotateX(${(-y * 5).toFixed(2)}deg) rotateY(${(x * 6).toFixed(2)}deg) translateY(-4px)`;
    });
  }
})();
